import React, { useEffect, useState, useRef } from "react";
import LoadingOverlay from "react-loading-overlay";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import Cookies from "js-cookie";
import * as Constants from "../../constants/Constants";
import { LOCK_ICON, UNLOCK_ICON } from "../../assets/js/image";
import "./DocumentLibrary.css";

import ErrorContainer from "../Common/Errors/ErrorContainer";

import {
  httpGetDocumentLibraryList,
  httpGetDocumentLibraryDocLinkById,
} from "../../service/httpClient";

const DocumentLibraryContainer = () => {
  const gridRef = useRef();
  const domainHostUrl = window?.location?.origin || "";
  const [responseData, setResponseData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");
  const [updateStatus, setUpdateStatus] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [errorStatus, setErrorStatus] = useState(false);

  const loginCellRenderer = (param) => {
    return param?.value ? (
      !Cookies.get("userName") ? (
        <a className="gridCellLink" href="/login">
          <LOCK_ICON style="loginIcon" />
        </a>
      ) : (
        <UNLOCK_ICON style="loginIcon" />
      )
    ) : (
      ""
    );
  };

  const handleGetDocumentLinkById = (id, documentName) => {
    try {
      httpGetDocumentLibraryDocLinkById(id)
        .then((response) => {
          const { data = [], status, headers } = response || {};

          if (data && status === 200) {
            let contentType =
              typeof headers["content-type"] !== "undefined"
                ? headers["content-type"]
                : "application/octet-stream";

            let fileType =
              typeof Constants.FILE_MIME_TYPE[contentType] !== "undefined"
                ? Constants.FILE_MIME_TYPE[contentType]
                : "";
            let tmpFileName = `document-${documentName
              .toLowerCase()
              .replace(/ /g, "-")}`;

            let fileDt = new Blob([response.data], {
              type: contentType,
              encoding: "UTF-8",
            });
            const url = window.URL.createObjectURL(fileDt);
            const link = document.createElement("a");
            link.href = url;
            if (fileType) {
              link.setAttribute("download", `${tmpFileName}.${fileType}`);
            } else {
              link.setAttribute("target", "_blank");
            }
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            link.remove();
          } else {
            setErrorMsg(Constants.ERROR_UNABLE_TO_DOWNLOAD_FILE);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setIsLoading(false);
        });
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  const documentCellRenderer = (params) => {
    const { data = {} } = params || {};
    const { loginRequired: login = "", id = "", documentName = "" } =
      data || {};
    return (
      <div>
        {((login && Cookies.get("userName")) || !login) && id ? (
          <span
            className="gridCellLink"
            onClick={() => handleGetDocumentLinkById(id, documentName)}
          >
            {params?.value}
          </span>
        ) : (
          params?.value
        )}
      </div>
    );
  };

  const columnDefs = [
    {
      headerName: " ",
      field: "documentCategory",
      width: 50,
      rowGroup: true,
      hide: true,
      filter: true,
      sortable: true,
    },
    {
      headerName: "Login",
      field: "loginRequired",
      width: 100,
      cellRenderer: loginCellRenderer,
      filter: false,
      sortable: false,
    },
    {
      headerName: "Document",
      field: "documentName",
      flex: 1,
      width: 80,
      cellRenderer: documentCellRenderer,
    },
    {
      headerName: "Published",
      field: "documentPublished",
      width: 140,
      cellRenderer: (params) => {
        const { data = {} } = params || {};
        const { documentPublished = "" } = data || {};
        if (documentPublished) {
          let d = new Date(documentPublished);
          const date = d.toISOString().split("T")[0];
          const time = d.toTimeString().split(" ")[0];
          return `${date} ${time}`;
        } else {
          return "";
        }
      },
    },
    {
      headerName: "Description",
      field: "documentDescription",
      width: 500,
      sortable: true,
      filter: true,
    },
  ];
  const defaultColDef = {
    lockVisible: true,
    suppressMovable: true,
    enablePivot: true,
    filter: true,
    sortable: true,
    resizable: true,
  };

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    try {
      httpGetDocumentLibraryList()
        .then((response) => {
          const { data = [], status } = response || {};
          if (data && data?.length > 0 && status === 200) {
            setResponseData(data);
          } else {
            setResponseData([]);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.log(error);
          setIsLoading(false);
        });
    } catch (error) {
      console.log(error);
      setIsLoading(false);
    }
  };

  return (
    <div className="gridMainContainer">
      <div className="m-top-10">
        <ErrorContainer
          errorMsg={errorMsg}
          updateStatus={updateStatus}
          successMsg={successMsg}
          errorStatus={errorStatus}
          errorStyle="errorMessage"
          successStyle="successMessage"
        />
      </div>
      <LoadingOverlay active={isLoading} spinner>
        <div className="docLibInstruct">
          <ul>
            <li>Click a category name to view a list of resource documents.</li>
            <li>Click a document link to open and view the contents.</li>
            {!Cookies.get("userName") && (
              <li>
                If a<LOCK_ICON style="loginIcon" /> is displayed in the login
                column, you will be required to login to{" "}
                <a className="clsLink" href="/login">
                  Vendor Management System
                </a>{" "}
                to view the document.
              </li>
            )}
          </ul>
        </div>

        <div className="gridHeading">
          {Constants.DOCUMENT_LIBRARY} ({responseData && responseData?.length})
        </div>

        <div className="ag-theme-balham gridContainer">
          <AgGridReact
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={responseData}
            defaultColDef={defaultColDef}
            overlayNoRowsTemplate={
              responseData.length > 0 ? "" : Constants.NO_DATA_FOUND
            }
          />
        </div>
      </LoadingOverlay>
    </div>
  );
};

export default DocumentLibraryContainer;
